import i18n, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * i18n — Greek first (formal "εσείς"), English second.
 *
 * Namespaces are discovered automatically: drop `src/i18n/el/<ns>.json` + `src/i18n/en/<ns>.json`
 * and use `useTranslation('<ns>')`. No registry to edit. `common` is the default namespace.
 */
const modules = import.meta.glob<{ default: Record<string, unknown> }>('./{el,en}/*.json', {
  eager: true,
});

const resources: Resource = { el: {}, en: {} };
for (const [path, mod] of Object.entries(modules)) {
  const m = path.match(/\.\/(el|en)\/([\w-]+)\.json$/);
  if (!m) continue;
  resources[m[1]!]![m[2]!] = mod.default;
}

export const LANGS = ['el', 'en'] as const;
export type Lang = (typeof LANGS)[number];
const KEY = 'gh.lang';

function initialLang(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'el' || v === 'en') return v;
  } catch {
    /* ignore */
  }
  return 'el';
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLang(),
  fallbackLng: 'el',
  supportedLngs: LANGS,
  defaultNS: 'common',
  ns: Object.keys(resources.el ?? {}),
  interpolation: { escapeValue: false },
  returnNull: false,
  react: { useSuspense: false },
});

i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
  try {
    localStorage.setItem(KEY, lng);
  } catch {
    /* ignore */
  }
});

export function setLanguage(lang: Lang) {
  return i18n.changeLanguage(lang);
}

export default i18n;
